import { Absence, AbsenceType, ResidentDtos, User } from '@lmc/ts-common'
import { getDateTimeInZone, ISO_STRING_FORMAT } from '../utils.datetime'
import { RotaInstanceStatus } from '../types'
import { statusToColorMap } from '../RotaBuilder/common/constants'
import dayjs from 'dayjs'
import _ from 'lodash'
import { SHIFT } from '../constants'
import {
  RotaEventInstance,
  RotaResidentEventInstance,
  RotaShiftEventInstance,
  RotaUserEventInstance,
} from '../types'

export const UNASSIGNED_ID = 'unassigned'

export const getTransformedEventUsers = (users: User[]) =>
  users?.map((user) => ({
    id: user.id,
    user,
  }))

export const getTransformedEventResidents = (
  residents: ResidentDtos[],
  events: RotaResidentEventInstance[]
) => {
  const uniqueResidents = new Map(
    residents?.map((resident) => [
      resident.id,
      { id: resident.id, resident, isDeletedSU: false },
    ])
  )

  events.forEach((event) => {
    if (event.deletedSU && !uniqueResidents.has(event.subjectId)) {
      uniqueResidents.set(event.subjectId, {
        id: event.subjectId,
        resident: {} as ResidentDtos,
        isDeletedSU: true,
      })
    }
  })

  return Array.from(uniqueResidents.values())
}

export const getTransformedEventShifts = (
  events: RotaEventInstance[]
): RotaShiftEventInstance[] => {
  const groupedShifts = _.groupBy(events, 'instanceType')

  const keys = Object.keys(groupedShifts)

	const transformedShifts: RotaShiftEventInstance[] = keys?.map((key) => {
    const shifts = groupedShifts?.[key] || [];
    
    const fullyAllocated = shifts.reduce((count, shift) => {
      return (shift?.numberSupply ?? 0) <= (shift?.teamMembers?.length ?? 0) ? count + 1 : count;
    }, 0);
  
    return {
      id: key,
      type: SHIFT,
      shifts,
      totalShifts: shifts.length,
      fullyAllocated
    };
  });

  return transformedShifts
}

const roundToNearestHalfOrInteger = (value: number) => {
  return Math.round(value * 2) / 2
}

export const getInvalidEventsFromAbsences = (
  absences: Absence[],
  absenceTypes: AbsenceType[]
) =>
  absences?.map((absence) => {
    // @ts-ignore
    const start = getDateTimeInZone(absence.startDate).format(ISO_STRING_FORMAT)
    // @ts-ignore
    const end = getDateTimeInZone(absence.endDate).format(ISO_STRING_FORMAT)
    const absenceTypeName =
      absenceTypes?.find(
        (absenceType) => absenceType.id === absence.absenceType
      )?.name || 'Absence'
    const duration = roundToNearestHalfOrInteger(
      dayjs(end).diff(start, 'day', true)
    )
    const absenceTitle = `${absenceTypeName} · ${duration} days`
    return {
      start,
      end,
      resource: absence.userId,
      title: absenceTitle,
      cssClass: 'absence',
    }
  })

export const getTransformedResidentEventsData = (
  events: RotaEventInstance[]
): RotaResidentEventInstance[] => {
  return events?.map((event) => {
    if (!event?.id) {
      return {} as RotaResidentEventInstance
    }

    return {
      ...event,
      id: event?.id,
      start: getDateTimeInZone(event.startTime).format(ISO_STRING_FORMAT),
      end: getDateTimeInZone(event.endTime).format(ISO_STRING_FORMAT),
      title: event?.title,
      subjectId: event.subjectId,
      resource: event.subjectId || UNASSIGNED_ID,
      color: statusToColorMap[event.status as unknown as RotaInstanceStatus],
      deletedSU: !!event.deletedServiceUser,
    }
  })
}

const getEventResources = (event: RotaEventInstance) => {
  const allocatedTeamMembers =
    event.teamMembers?.map(
      (member: { teamMemberId: string }) => member.teamMemberId
    ) || []

  if (allocatedTeamMembers.length < event.numberSupply) {
    return [...allocatedTeamMembers, UNASSIGNED_ID]
  }

  return allocatedTeamMembers
}

export const getTransformedUserEventsData = (
  events: RotaEventInstance[]
): RotaUserEventInstance[] => {
  return events?.map((event) => {
    if (!event?.id) {
      return {} as RotaUserEventInstance
    }

    return {
      ...event,
      id: event?.id,
      start: getDateTimeInZone(event.startTime).format(ISO_STRING_FORMAT),
      end: getDateTimeInZone(event.endTime).format(ISO_STRING_FORMAT),
      title: event?.title,
      subjectId: event.subjectId,
      resource: getEventResources(event),
      color: statusToColorMap[event.status as unknown as RotaInstanceStatus],
    }
  })
}
